import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Facebook, Share2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import ManifestoCard from "@/components/ManifestoCard";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LangContext";

export default function ManifestoPage() {
  const { id } = useParams();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [items, setItems] = useState([]);
  const [single, setSingle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const c = await api.get("/content");
        if (!alive) return;
        if (!c.data?.manifesto_enabled) {
          // module disabled → kick out
          navigate("/", { replace: true });
          return;
        }
        setContent(c.data);
        if (id) {
          const r = await api.get(`/manifesti/${id}`);
          setSingle(r.data);
        } else {
          const r = await api.get("/manifesti");
          setItems(r.data);
        }
      } catch (err) {
        if (err?.response?.status === 404 && id) {
          navigate("/manifesto", { replace: true });
          return;
        }
        toast.error(formatApiError(err));
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-afb-bg">
        <div className="text-afb-muted text-sm tracking-widest uppercase">
          Caricamento...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-afb-bg min-h-screen" data-testid="manifesto-page">
      <Seo
        content={content}
        overrides={
          single
            ? {
                title: `${[single.name_first, single.name_last]
                  .filter(Boolean)
                  .join(" ")} — ${lang === "it" ? "Manifesto Funebre" : "Funeral Notice"}`,
                description: single.announce_text || single.intro_text || "",
                og_image: single.photo_url || single.decoration_url || "",
              }
            : {
                title:
                  lang === "it"
                    ? "Manifesti Funebri — Agenzia Funebre Barone"
                    : "Funeral Notices — Barone Funeral Agency",
              }
        }
      />
      <Header
        manifestoEnabled={!!content?.manifesto_enabled}
        bookingEnabled={!!content?.booking_enabled}
        bookingLabel={
          lang === "en" ? content?.booking_label_en : content?.booking_label_it
        }
      />

      <main className="pt-28 sm:pt-32 pb-20">
        <div className="afb-container">
          <Link
            to={id ? "/manifesto" : "/"}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-afb-muted hover:text-afb-gold mb-8"
            data-testid="manifesto-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {id
              ? lang === "it"
                ? "Tutti i manifesti"
                : "All notices"
              : lang === "it"
                ? "Torna alla home"
                : "Back to home"}
          </Link>

          <div className="max-w-2xl mb-10 sm:mb-14">
            <span className="afb-eyebrow">
              {lang === "it" ? "Manifesti Funebri" : "Funeral Notices"}
            </span>
            <h1 className="serif text-[1.9rem] sm:text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.15] text-afb-ink">
              {single
                ? `${single.name_first || ""} ${single.name_last || ""}`.trim() ||
                  (lang === "it" ? "Manifesto" : "Notice")
                : lang === "it"
                  ? "I nostri manifesti"
                  : "Our notices"}
            </h1>
            <span className="block mt-5 h-[2px] w-16 sm:w-20 bg-[color:var(--afb-gold)]" />
          </div>

          {single ? (
            <div className="max-w-3xl mx-auto">
              <ManifestoCard data={single} />
              <ShareBar manifesto={single} />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 text-afb-muted">
              {lang === "it"
                ? "Al momento non ci sono manifesti pubblicati."
                : "There are no published notices at the moment."}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {items.map((m) => (
                <Link
                  key={m.id}
                  to={`/manifesto/${m.id}`}
                  className="block hover:-translate-y-0.5 transition-transform"
                  data-testid={`manifesto-link-${m.id}`}
                >
                  <ManifestoCard data={m} compact />
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer content={content} />
      <WhatsAppFab content={content} />
    </div>
  );
}

function ShareBar({ manifesto }) {
  const { lang } = useLang();
  const [shareUrl, setShareUrl] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") setShareUrl(window.location.href);
  }, []);

  const fullName = [manifesto.name_first, manifesto.name_last]
    .filter(Boolean)
    .join(" ");
  const text =
    lang === "it"
      ? `Manifesto Funebre — ${fullName} · Onoranze Funebri Barone`
      : `Funeral Notice — ${fullName} · Barone Funeral Service`;

  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    shareUrl
  )}&quote=${encodeURIComponent(text)}`;

  const messengerHref = `fb-messenger://share/?link=${encodeURIComponent(
    shareUrl
  )}`;

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(
    `${text}\n${shareUrl}`
  )}`;

  const handleNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: text, url: shareUrl });
      } catch {
        /* user cancelled */
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <div
      className="mt-6 sm:mt-8 flex flex-wrap items-center justify-center gap-3 sm:gap-4"
      data-testid="manifesto-sharebar"
    >
      <span className="text-xs uppercase tracking-[0.24em] text-afb-muted mr-2">
        {lang === "it" ? "Condividi" : "Share"}
      </span>

      <a
        href={fbHref}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1877F2] hover:bg-[#0f6ae0] text-white text-sm font-medium shadow-sm transition-colors"
        data-testid="share-facebook"
      >
        <Facebook className="w-4 h-4" />
        {lang === "it" ? "Condividi su Facebook" : "Share on Facebook"}
      </a>

      <a
        href={messengerHref}
        className="inline-flex sm:hidden items-center gap-2 px-5 py-2.5 rounded-full border border-[#1877F2] text-[#1877F2] text-sm font-medium"
        data-testid="share-messenger"
      >
        Messenger
      </a>

      <a
        href={whatsappHref}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1fb557] text-white text-sm font-medium shadow-sm transition-colors"
        data-testid="share-whatsapp"
      >
        WhatsApp
      </a>

      <button
        type="button"
        onClick={handleNative}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[color:var(--afb-cream)] text-afb-ink text-sm hover:bg-afb-cream/50 transition-colors"
        data-testid="share-more"
      >
        <Share2 className="w-4 h-4" />
        {lang === "it" ? "Altro" : "More"}
      </button>
    </div>
  );
}
